import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import UpcastWriter from '@ckeditor/ckeditor5-engine/src/view/upcastwriter';

import {
  checkSelectedLink,
  checkSelectedKeyword,
  checkForMedia,
  checkForHeader,
  checkForEditability,
  checkForSpan,
  checkForSubtemplate,
  checkForFigCaption,
  CkeditorConstants,
  randomUUIDGenerator,
  isLinkElement
} from '../../src/utils';

const {
  attributeLinkOtherDocument,
  attributeWebLink,
  linksTo,
  class: { REF, WK_KEYWORD_REF, CK_MEDIA_AUDIO, CK_MEDIA_VIDEO, CK_MEDIA_IMAGE },
  event: { COPY_KEYWORD_FROM_KEYBOARD, PASTE_NOT_ALLOWED, PASTE_EXTERNAL_CONFIRMATION_REQUIRED },
  htmlAttributes: { REL, NUMBERED_CITATION_CLASS, CLASS, ID, ABOUT },
  helperAttributes: { SUBTEMPLATE_NAME, TYPE_OF, DATA_TENANT_ID, DATA_NODE_NAME, LINK_HREF },
  htmlElement: { SPAN, AUDIO, VIDEO, IMAGE, PARAGRAPH, TABLE, LINK_SPAN },
  htmlTagNames: { OL, UL },
  trackchangesMarkup,
  localStorageItem: { RHP_CITATION_REF_CLICK },
  pasteSourceMarker: { ATTRIBUTE: PASTE_SOURCE_ATTRIBUTE, INTERNAL: PASTE_SOURCE_INTERNAL_MARKER, MIME: PASTE_SOURCE_MIME },
  pasteSourceType: { INTERNAL: PASTE_SOURCE_INTERNAL, OFFICE: PASTE_SOURCE_OFFICE, EXTERNAL: PASTE_SOURCE_EXTERNAL },
  CONTENT_HARMONIZATION: { FRBR_EMBODIMENT, PCICORE_MEDIARESOURCE }
} = CkeditorConstants;
const items = [ AUDIO, VIDEO, IMAGE ];
export default class PasteUI extends Plugin {
  init() {
    const editor = this.editor;
    this.writer = new UpcastWriter( editor.editing.view.document );
    const clipboardPipeline = editor.plugins.get( 'ClipboardPipeline' );
    const editingView = editor.editing.view;

    const trackChangesMarkup = Object.values( trackchangesMarkup );

    this.getAttributesForAll( editor.model.schema );

    editingView.document.on( 'clipboardOutput', ( _, data ) => {
      const content = data.content;
      const selection = this.editor.model.document.selection;

      if ( selection.rangeCount === 1 && content.childCount ) {
        const items = Array.from( selection.getFirstRange().getItems() );
        const parent = items[ 0 ].parent;
        const isParentSame =
          new Set( items.map( item => item.parent ) ).size === 1;
        const isLinkParent = this.isLink( parent );

        /**
         * If copied content is only one text then check if parent element is link element
         * If copied content has more than one text then check if all parents are same and link element
         */
        if ( items.length <= 1 ? isLinkParent : isParentSame && isLinkParent ) {
          /**
           * Create copy of parent link element and wrap content with new link
           */
          data.content = this.createLinkElement(
            this.copyAttributes( parent ),
            Array.from( content.getChildren() )
          );
        }
      }

      /**
          * Create copy of parent IST element and wrap content
       */
      const range = selection.getFirstRange();
      const inlineSubtemplateObj = this.checkIsSingleISTSelected( range, data.content );
      if ( inlineSubtemplateObj.isInlineSubtemplate ) {
        data.content = this.createLinkElement(
          this.copyAttributes( inlineSubtemplateObj.inlineSubtemplateParent ),
          Array.from( content.getChildren() )
        );
      }

      /**
       * Tag the outgoing clipboard content so it can be recognised as originating
       * from our own editor when it is pasted back (across documents/tabs/browsers).
       */
      const clipboardMethod = data?.method;
      const isCopyOrCut = !clipboardMethod || clipboardMethod === 'copy' || clipboardMethod === 'cut';
      if ( data.dataTransfer && isCopyOrCut ) {
        data.dataTransfer.setData( PASTE_SOURCE_MIME, PASTE_SOURCE_INTERNAL_MARKER );
      }
      this.tagContentAsInternal( data.content );
    } );

    clipboardPipeline.on( 'inputTransformation', ( evt, data ) => {
      /**
       * Identify where the pasted content came from:
       * - internal: copied from within our own editor/site -> keep existing paste logic
       * - office: pasted from an office document -> keep existing office logic
       * - external: any other source -> require user confirmation before plain-text insertion
       */
      const pasteSource = this.getPasteSource( data );
      if ( pasteSource === PASTE_SOURCE_EXTERNAL ) {
        const plainText = this.getPlainTextFromDataTransfer( data?.dataTransfer );
        evt?.stop?.();
        const html = ( data?.dataTransfer?.getData?.( 'text/html' ) || '' ).trim();
        const text = ( data?.dataTransfer?.getData?.( 'text/plain' ) || '' ).trim();
        // Plain if there's no HTML, or the HTML carries no formatting beyond trivial wrappers.
        const isPlainTextSource = !html;
        if ( isPlainTextSource && text ) {
          this.insertPlainText( plainText );
        } else {
          this.editor.fire( PASTE_EXTERNAL_CONFIRMATION_REQUIRED, { plainText, pasteSource } );
        }
        return;
      }
      if ( pasteSource === PASTE_SOURCE_INTERNAL ) {
        this.removePasteSourceMarkerFromView( data.content );
      }

      if ( this._hasNumberedReferenceContent( data ) && this._selectionTouchesLink() ) {
        this.evtStop( evt );
      }

      if ( this._hasLinkContent( data ) && this._selectionTouchesLink() ) {
        this.evtStop( evt );
        return;
      }

      const isNotEditable = checkForEditability( this.editor );
      // Get the DataProcessor module from the editor instance
      const dataProcessor = editor.data.processor;
      // Convert the DocumentFragment to a string
      let htmlString = dataProcessor.toData( data.content );
      if ( data._isTransformedWithPasteFromOffice && this.hasListElement( data.content ) ) {
        const validationChk = this.chkForPasteFromOfficeValidation( this.editor );
        if ( validationChk || isNotEditable ) {
          this.evtStop( evt );
        } else {
          htmlString = htmlString.replace( /<span[^>]*>(?:\s*<br\s*\/?>\s*)*<\/span>/gi, '' );
          htmlString = htmlString.replace(
            /<span\s+lang[^>]*>(.*?)<\/span>/g,
            '$1'
          );

          const regex = /<span[^>]*>(.*?)<\/span>/g;
          let match;
          do {
            match = htmlString.match( regex );
            if ( match ) {
              htmlString = htmlString.replace( regex, '$1' );
            }
          } while ( match );
          // Clean Office-specific formatting: preserve ul styles, remove all others
          htmlString = this.removeOfficeFormatting( htmlString );

          data.content = dataProcessor.toView( htmlString );

          // Insert ul li on next or prev line if cursor is at end or start respectively
          const selection = this.editor.model.document.selection;

          this.editor.model.change( writer => {
            const range = selection && selection.getFirstRange();
            const parent = selection && selection.anchor.parent;
            const currentPosition = selection && selection.getFirstPosition();
            if ( range && range.isCollapsed && !range.start.parent.isEmpty && parent ) {
              const paragraph = writer.createElement( PARAGRAPH );
              let insertionPosition = null;
              if ( currentPosition.isAtEnd ) {
                insertionPosition = 'after';
              } else if ( currentPosition.isAtStart ) {
                insertionPosition = 'before';
              }
              if ( insertionPosition !== null ) {
                writer.insert( paragraph, parent, insertionPosition );
              } else {
                return;
              }
              const afterPos = writer.createPositionAt( paragraph, 0 );
              editor.focus();
              writer.setSelection( afterPos );
            }
          } );
        }
        return;
      }
      if ( this._hasMediaElement( data.content ) ) {
        const dataTransfer = this.stripPasteSourceMarker( data.dataTransfer?.getData( 'text/html' ) ) ||
          data.dataTransfer?.getData( 'text/plain' ) || '';
        const viewFragment = dataTransfer ?
          this.editor.data.htmlProcessor.toView( dataTransfer ) :
          data.content;
        this._regenerateAllMediaIds( viewFragment );
        const tagName = this._getMediaElement( viewFragment );
        const htmlForCheck = this.editor.data.processor.toData( viewFragment );
        // check for block image when copy pasting block images as inline
        const isBlockImgOutsideTable =
          /<figure[\s\S]*?<img[\s\S]*?<\/figure>/i.test( htmlForCheck ) &&
          !/<table[\s\S]*?<figure[\s\S]*?img[\s\S]*?<\/figure>[\s\S]*?<\/table>/i.test( htmlForCheck );
        if ( tagName && tagName.toLowerCase() === IMAGE && isBlockImgOutsideTable ) {
          evt.stop();
          this._updateImageRelatedId( viewFragment );
          return;
        }
        if ( tagName && ( tagName.toLowerCase() === VIDEO || tagName.toLowerCase() === AUDIO ) ) {
          this._updateVideoRelatedId( viewFragment );
        }
        data.content = viewFragment;
      } else {
        if ( isNotEditable ) {
          evt.stop();
        }

        const dataTransfer = data.dataTransfer;
        let content = this.stripPasteSourceMarker( dataTransfer?.getData?.( 'text/html' ) ) ||
          dataTransfer?.getData?.( 'text/plain' ) ||
          this.editor.data.processor.toData( data.content );
        // On paste(ctrl + v) remove all release note pin
        content = content?.replace( /<span\s+[^>]*\brel="skos:editorialNote"[^>]*>.*?<\/span>/gs, '' );
        const contentView = this.editor.data.htmlProcessor.toView( content );
        this.changeLinkElementsIds( contentView._children );

        /**
         * Removes trackchanges attributes in citation/link/keyword element from clipboard during paste
         */
        for ( const itemsInClipBoard of contentView.getChildren() ) {
          let isTrackChangeDataFound = false;

          /**
           * Checks is item in clipboard is a citation/link/keyword
           */
          const items = [ CK_MEDIA_AUDIO, CK_MEDIA_VIDEO, CK_MEDIA_IMAGE ];
          let mediaExist = false;
          items.forEach( ele => {
            if ( !itemsInClipBoard._textData && [ ...itemsInClipBoard.getClassNames() ].includes( ele ) ) {
              mediaExist = true;
            }
          } );
          if ( itemsInClipBoard.name === SPAN &&
            ( [ ...itemsInClipBoard.getClassNames() ].includes( REF ) ||
              [ ...itemsInClipBoard.getClassNames() ].includes( WK_KEYWORD_REF ) || mediaExist ) ) {
            for ( const attributePair of itemsInClipBoard.getAttributes() ) {
              /**
             * Check if citation/link/keyword contains trackchanges attributes
             */
              if ( trackChangesMarkup.includes( attributePair[ 0 ] ) ) {
                isTrackChangeDataFound = true;
                break;
              }
            }
          }
          if (
            isTrackChangeDataFound
          ) {
            /**
             * Removes trackchanges attributes of citation/link/keyword in clipboard
             */
            trackChangesMarkup.forEach( trackChangesAttribute => {
              if ( itemsInClipBoard.hasAttribute( trackChangesAttribute ) ) {
                itemsInClipBoard._removeAttribute( trackChangesAttribute );
              }
            } );
          }

          /**
           * Update the store if a keyword is copied using Ctrl + C and Ctrl + V
           */
          if ( itemsInClipBoard.name === SPAN &&
            [ ...itemsInClipBoard.getClassNames() ].includes( WK_KEYWORD_REF ) ) {
            this.editor.fire( COPY_KEYWORD_FROM_KEYBOARD, itemsInClipBoard );
          }
        }
        data.content = contentView;
      }
    } );
  }

  /**
     This function return object with true/false value and IST parent
     if single Inline Subtemplate is selected.
  */
  checkIsSingleISTSelected( range, content ) {
    const checkForSpan = this.checkIfAllContentItemsAreSpan( content );
    const isISTObject = { isInlineSubtemplate: false, inlineSubtemplateParent: null };
    const isFind = [ ...range.getItems() ].find(
      item => item.parent.name === SPAN &&
        item.parent._attrs.get( SUBTEMPLATE_NAME ) &&
        checkForSpan
    );
    return isFind ? ( {
      isInlineSubtemplate: true, inlineSubtemplateParent: isFind.parent
    } ) : isISTObject;
  }

  /**
     This function return object with true/false value if selected data.content have all span childrens
  */
  checkIfAllContentItemsAreSpan( content ) {
    let hasSpanChildren = false;
    if ( content && content._children ) {
      const contentChildrenNameList = content._children.map( item => item.name );
      hasSpanChildren = contentChildrenNameList.every( val => val === SPAN );
    }
    return hasSpanChildren;
  }

  getAttributesForAll( schema ) {
    schema.addAttributeCheck( context => {
      for ( const element of [ SPAN ] ) {
        if ( context.endsWith( element ) ) {
          return true;
        }
      }
    } );
  }

  createLinkElement( attributes, children ) {
    return this.writer.createElement( SPAN, attributes, children );
  }

  copyAttributes( element ) {
    return Array.from( element.getAttributes() ).reduce( ( curr, prop ) => {
      curr[ prop[ 0 ] ] = prop[ 1 ];
      return curr;
    }, {} );
  }

  changeLinkElementsIds( elements ) {
    elements.forEach( element => {
      if (
        ( element.name && element.hasAttribute( REL ) && this.isLink( element ) ) ||
        (
          element && element._children && element._children[ 0 ] &&
          element._children[ 0 ].name && items.includes( element._children[ 0 ].name )
        )
      ) {
        this.resetLinkId( element );
      }

      if (
        element && element._children && element._children[ 0 ] && element._children.length &&
        element._children[ 0 ].name !== AUDIO && element._children[ 0 ].name !== VIDEO && element._children[ 0 ].name !== IMAGE
      ) {
        this.changeLinkElementsIds( element._children );
      }
    } );
  }

  isLink( element ) {
    if ( element ) {
      const attributesMap = new Map( element.getAttributes() );
      const typeofValue = attributesMap.get( TYPE_OF );
      const classList = attributesMap.get( CLASS );
      const isWebTypeLink = typeofValue === attributeWebLink;
      const isDocumentTypeLink =
        typeofValue === attributeLinkOtherDocument &&
        classList &&
        !classList.includes( NUMBERED_CITATION_CLASS );
      const isKeyword = classList && classList.includes( WK_KEYWORD_REF );
      const isMedia = element && element._children && element._children[ 0 ] &&
        element._children[ 0 ].name && items.includes( element._children[ 0 ].name );
      return isWebTypeLink || isDocumentTypeLink || isKeyword || isMedia;
    } else {
      return null;
    }
  }

  resetLinkId( element ) {
    const newId = randomUUIDGenerator();
    const oldId = element.getAttribute( 'id' );
    if ( element._children[ 0 ].name && items.includes( element._children[ 0 ].name ) ) {
      this.writer.setAttribute( 'id', newId, element._children[ 0 ] );
    }
    else {
      this.writer.setAttribute( 'id', newId, element );
      const aboutAttr = element.getAttribute( ABOUT );
      const rhpCitationRefClicked = JSON.parse( localStorage.getItem( RHP_CITATION_REF_CLICK ) );
      if ( rhpCitationRefClicked ) {
        if ( aboutAttr ) {
          this.writer.removeAttribute( ABOUT, element );
        }
      } else {
        if ( aboutAttr ) {
          this.writer.setAttribute( ABOUT, aboutAttr.replace( oldId, newId ), element );
        } else {
          this.writer.setAttribute( ABOUT, `${ this.editor.baseHrefForLinkPlugin }#${ newId }`, element );
        }
      }
    }
  }

  /**
   *
   * @param {Editor Instance} editor
   * @returns true | false if paste is allowed in selection on editor
   */
  chkForPasteFromOfficeValidation( editor ) {
    const isWholeLinkSelected = checkSelectedLink( editor, null );
    const isKeywordSelected = checkSelectedKeyword( editor );
    const isMedia = checkForMedia( editor );
    const isHeader = checkForHeader( editor );
    const isSpan = checkForSpan( editor );
    const checkForSubtemplates = checkForSubtemplate( this.editor );
    const isFigCaption = checkForFigCaption( this.editor );
    return (
      isWholeLinkSelected || isKeywordSelected || isWholeLinkSelected != undefined ||
      isMedia || isHeader || isSpan || checkForSubtemplates || isFigCaption
    );
  }

  /**
  *
  * @param {documentFrament that is pasted} fragment
  * @returns true/false if fragment has ul or ol
  */
  hasListElement( fragment ) {
    const children = fragment?._children || [];
    if ( !children.length ) {
      return false;
    }
    return children.every( node => node?.is?.( 'element' ) && ( node.name === UL || node.name === OL ) );
  }

  /**
   *
   * @param {Event} evt
   * Stop event of paste and show notification
   */
  evtStop( evt ) {
    evt?.stop?.();
    this.editor.fire( PASTE_NOT_ALLOWED );
  }

  /**
   * Tags every top-level element of the outgoing clipboard content with an internal
   * marker attribute. The marker is serialized into the clipboard HTML so the content
   * can be recognised as internal when pasted back, regardless of document, tab or browser.
   * @param {ViewDocumentFragment} content
   */
  tagContentAsInternal( content ) {
    if ( !content?.getChildren ) {
      return;
    }
    for ( const child of Array.from( content.getChildren() ) ) {
      if ( child?.is?.( 'element' ) ) {
        this.writer.setAttribute( PASTE_SOURCE_ATTRIBUTE, PASTE_SOURCE_INTERNAL_MARKER, child );
      }
    }
  }

  /**
   * Determines the origin of the pasted content.
   * @param {Object} data inputTransformation event data
   * @returns {string} one of PASTE_SOURCE_OFFICE | PASTE_SOURCE_INTERNAL | PASTE_SOURCE_EXTERNAL
   */
  getPasteSource( data ) {
    if ( data._isTransformedWithPasteFromOffice ) {
      // Mini-editor/RHP instances always paste as plain text, even for Office lists; only the main editor preserves Office list formatting.
      const preservesOfficeLists = !this.isMiniOrRhpEditor() && this.hasListElement( data.content );
      return preservesOfficeLists ? PASTE_SOURCE_OFFICE : PASTE_SOURCE_EXTERNAL;
    }
    if ( this.isFromOurSite( data ) ) {
      return PASTE_SOURCE_INTERNAL;
    }
    return PASTE_SOURCE_EXTERNAL;
  }

  /**
   * Mini-editors (citations, keyword/definition RHP editors, release notes RHP) are all
   * configured with `type: 'mini-editor'`; the main document editor does not set this.
   * @returns {boolean}
   */
  isMiniOrRhpEditor() {
    return this.editor.config.get( 'type' ) === 'mini-editor';
  }

  /**
   * Checks whether the pasted content originated from our own editor/site by looking for
   * the internal marker or any of our known internal content signatures in the clipboard HTML.
   * @param {Object} data inputTransformation event data
   * @returns {boolean}
   */
  isFromOurSite( data ) {
    /**
     * Fast, reliable path for same-browser paste (covers partial/inline text copies that have
     * no top-level element to carry the HTML marker): a custom clipboard MIME type.
     */
    if ( data?.dataTransfer?.getData?.( PASTE_SOURCE_MIME ) === PASTE_SOURCE_INTERNAL_MARKER ) {
      return true;
    }
    const clipboardHtml = data?.dataTransfer?.getData?.( 'text/html' ) || '';
    if ( !clipboardHtml ) {
      return false;
    }
    const parsedBody = new DOMParser().parseFromString( clipboardHtml, 'text/html' ).body;
    if ( parsedBody.querySelector( `[${ PASTE_SOURCE_ATTRIBUTE }]` ) ) {
      return true;
    }
    return Boolean( parsedBody.querySelector( this.getInternalContentSelector() ) );
  }

  /**
   * Builds a CSS selector matching content that can only be produced by our own editor.
   * Used as a fallback so previously copied internal content (without the marker) is still
   * recognised as internal.
   * @returns {string}
   */
  getInternalContentSelector() {
    return [
      `[${ TYPE_OF }^="pcicore:"]`,
      `[${ REL }="${ linksTo }"]`,
      `span.${ REF }`,
      `span.${ WK_KEYWORD_REF }`,
      `.${ NUMBERED_CITATION_CLASS }`,
      `[${ SUBTEMPLATE_NAME }]`,
      `[${ DATA_TENANT_ID }]`,
      `.${ CK_MEDIA_IMAGE }`,
      `.${ CK_MEDIA_AUDIO }`,
      `.${ CK_MEDIA_VIDEO }`
    ].join( ',' );
  }

  /**
   * Converts external clipboard content to plain text, discarding all HTML attributes/markup.
   * @param {Event} evt inputTransformation event
   * @param {Object} data inputTransformation event data
   */
  getPlainTextFromDataTransfer( dataTransfer ) {
    if ( !dataTransfer?.getData ) {
      return '';
    }
    const plainText = dataTransfer.getData( 'text/plain' );
    const leadingBulletPattern = new RegExp(
      String.raw`^[ \t\u00A0\u202F]*` +
      String.raw`(?:[\u2022\u25E6\u25AA\u25CF\u25CB\u25A0\u25A1\u2023\u2043\u2219\u00B7\uF0A7\uF0B7\uF06F][ \t\u00A0\u202F]*` +
      String.raw`(?:o[ \u00A0\u202F]*\t)?|o[ \u00A0\u202F]*\t)[ \t\u00A0\u202F]*`,
      'gm'
    );

    return plainText
      .replace( /\r\n?/g, '\n' )
      // Strip common bullets and Word-specific variants, with surrounding whitespace.
      .replace( leadingBulletPattern, '' )
      .replace( /[ \t]+$/gm, '' )
      .replace( /\n(?:[ \t]*\n){2,}/g, '\n\n' )
      .trim();
  }

  insertPlainText( plainText ) {
    const singleLineText = plainText?.replace( /\s+/g, ' ' ).trim();
    if ( !singleLineText || checkForEditability( this.editor ) ) {
      return;
    }
    const model = this.editor.model;

    model.change( writer => {
      const selection = model.document.selection;
      // A raw text node is never parsed, so markup-like text stays literal.
      if ( !selection.isCollapsed ) {
        model.deleteContent( selection );
      }
      model.insertContent( writer.createText( singleLineText ), selection );
    } );
  }

  /**
   * Removes the internal paste-source marker attribute from an HTML string so it is not
   * persisted in the document when internal content is pasted back.
   * @param {string} html
   * @returns {string}
   */
  stripPasteSourceMarker( html ) {
    if ( !html ) {
      return html;
    }
    return html.replace( new RegExp( `\\s*${ PASTE_SOURCE_ATTRIBUTE }="[^"]*"`, 'gi' ), '' );
  }

  /**
   * Removes Office-specific formatting attributes from HTML at any nesting level.
   * Preserves style="list-style-type:*" on <ul> elements only.
   * Removes ALL style attributes from <li>, <p>, <span>, and other elements.
   */
  removeOfficeFormatting( html ) {
    if ( !html ) {
      return html;
    }

    // Preserve only list-style-type on <ul>, remove all styles from other tags
    html = html.replace( /<ul([^>]*?)\s+style="([^"]*)"/gi, ( m, a, s ) => {
      const match = s.match( /list-style-type\s*:\s*([^;]+)/i );
      return `<ul${ a }${ match ? ` style="list-style-type:${ match[ 1 ].trim() }"` : '' }`;
    } );

    return html.replace( /<(?!ul[\s>])([a-z]+)([^>]*?)\s+style="[^"]*"([^>]*)>/gi, '<$1$2$3>' );
  }

  /**
   * Recursively removes the internal paste-source marker attribute from a view fragment.
   * @param {ViewNode} node
   */
  removePasteSourceMarkerFromView( node ) {
    if ( !node?.getChildren ) {
      return;
    }
    for ( const child of Array.from( node.getChildren() ) ) {
      if ( child?.is?.( 'element' ) ) {
        if ( child.hasAttribute( PASTE_SOURCE_ATTRIBUTE ) ) {
          this.writer.removeAttribute( PASTE_SOURCE_ATTRIBUTE, child );
        }
        this.removePasteSourceMarkerFromView( child );
      }
    }
  }

  _selectionTouchesLink() {
    const { selection } = this.editor.model.document;
    const selectionRangeCount = selection?.rangeCount;
    if ( !selectionRangeCount ) {
      return false;
    }

    return Array.from( selection.getRanges() ).some( range => {
      const rangeCollapsed = range.isCollapsed;
      if ( rangeCollapsed ) {
        const positionTouchesLink = this._positionTouchesLink( range.start );
        return positionTouchesLink;
      }
      const startTouchesLink = this._positionTouchesLink( range.start );
      const endTouchesLink = this._positionTouchesLink( range.end );
      return startTouchesLink || endTouchesLink;
    } );
  }

  _positionTouchesLink( position ) {
    if ( !position ) {
      return false;
    }
    const { model } = this.editor;
    const ancestorElements = position.parent?.getAncestors?.() || [];
    const linkContainer = [ position.textNode, position.parent, ...ancestorElements ].find(
      element => element && ( element.name === LINK_SPAN || isLinkElement( element ) )
    );
    if ( linkContainer ) {
      model.change( writer => {
        const afterLinkPosition = writer.createPositionAfter( linkContainer );
        writer.setSelection( afterLinkPosition );
      } );
      return false;
    }

    const nearbyNode = position.textNode || position.nodeBefore || position.nodeAfter || null;
    const isLinkTextNode = nearbyNode?.parent?.getAttribute?.( CLASS ) === REF ||
      isLinkElement( nearbyNode?.parent ) ||
      nearbyNode?.hasAttribute?.( LINK_HREF );

    if ( isLinkTextNode ) {
      return true;
    }
    return false;
  }

  _hasLinkContent( data ) {
    const clipboardHtml = data?.dataTransfer?.getData?.( 'text/html' ) || '';
    const transformedHtml = this.editor.data.processor.toData( data?.content );

    return [ clipboardHtml, transformedHtml ]
      .filter( Boolean )
      .some( html => {
        const parser = new DOMParser().parseFromString( html, 'text/html' );
        return Boolean(
          parser.body.querySelector( `span[rel="${ linksTo }"].${ REF }:not(.${ NUMBERED_CITATION_CLASS })` )
        );
      } );
  }

  _hasNumberedReferenceContent( data ) {
    const clipboardHtml = data?.dataTransfer?.getData?.( 'text/html' ) || '';
    const transformedHtml = this.editor.data.processor.toData( data.content );

    return [ clipboardHtml, transformedHtml ]
      .filter( Boolean )
      .some( html => {
        const parser = new DOMParser().parseFromString( html, 'text/html' );

        return Boolean(
          parser.body.querySelector( `.${ NUMBERED_CITATION_CLASS }` ) ||
          parser.body.querySelector( '[data-citationcount]' )
        );
      } );
  }

  /**
   * If pasted content has media element (IMAGE OR VIDEO) then update the id and about attribute
   * Iterate through child nodes
   * @param {*} fragment
   * @returns
   */

  _getMediaElement( fragment ) {
    const parser = new DOMParser().parseFromString(
      this.editor.data.processor.toData( fragment ),
      'text/html'
    );
    const element = parser.querySelector( IMAGE ) || parser.querySelector( VIDEO ) || parser.querySelector( AUDIO );
    return element?.tagName;
  }

  _hasMediaElement( fragment ) {
    if ( this._getMediaElement( fragment ) ) {
      return true;
    }
    return false;
  }

  _updateImageRelatedId( fragment ) {
    const isImageInsideTable = this.editor.data.processor.toData( fragment );
    if ( isImageInsideTable.includes( TABLE ) && isImageInsideTable.includes( IMAGE ) ) {
      return;
    }

    // If no image node found, then we proceed with manual insertimage
    const parser = new DOMParser().parseFromString( this.editor.data.processor.toData( fragment ), 'text/html' );
    const imgData = parser.querySelector( 'img' );

    this.editor.execute( 'insertimage', {
      value: {
        imageContent: [ {
          'src': imgData.getAttribute( 'src' ),
          'resource': imgData.getAttribute( 'src' ),
          'typeofnode': PCICORE_MEDIARESOURCE,
          'alt': imgData.alt || imgData.getAttribute( DATA_NODE_NAME ),
          'name': imgData.getAttribute( DATA_NODE_NAME ),
          'width': imgData.width,
          'height': imgData.height,
          'tenantId': imgData.getAttribute( DATA_TENANT_ID )
        } ]
      }
    } );
  }

  _updateVideoRelatedId( contentView ) {
    const isAudioOrVideoInsideTable = this.editor.data.processor.toData( contentView );
    if (
      isAudioOrVideoInsideTable.includes( TABLE ) &&
      ( isAudioOrVideoInsideTable.toString().includes( AUDIO ) ||
        isAudioOrVideoInsideTable.toString().includes( VIDEO ) )
    ) {
      return;
    }
    const id = randomUUIDGenerator();
    const about = `${ this.editor.baseHrefForLinkPlugin }#${ id }`;
    this.writer.setAttribute( ID, id, contentView._children[ 0 ] );
    this.writer.setAttribute( ABOUT, about, contentView._children[ 0 ] );
    contentView._children[ 0 ]._children.forEach( element => {
      if ( element._children[ 0 ].name === VIDEO ) {
        this.writer.setAttribute( ID, randomUUIDGenerator(), element._children[ 0 ] );
      }
      if ( element.getAttribute( REL ) === FRBR_EMBODIMENT ) {
        const oldAbt = element.getAttribute( ABOUT );
        let abt = oldAbt.split( '/' );
        const tenantId = element.parent._children[ 0 ]._children[ 0 ]?._children[ 0 ]?.getAttribute( DATA_TENANT_ID )?.split( '/' ).pop();
        abt[ abt.length - 1 ] = tenantId ? tenantId.concat( '-1000' ) : '';
        abt = abt.join( '/' );
        this.writer.setAttribute( ABOUT, abt, element );
      }
    } );
  }

  _regenerateAllMediaIds( fragment ) {
    const html = this.editor.data.processor.toData( fragment );
    const parser = new DOMParser().parseFromString( html, 'text/html' );
    const mediaTags = [ IMAGE, AUDIO, VIDEO ];
    mediaTags.forEach( tag => {
      const elements = parser.querySelectorAll( tag );
      elements.forEach( el => {
        const newId = randomUUIDGenerator();
        el.setAttribute( 'id', newId );

        if ( el.hasAttribute( ABOUT ) ) {
          const oldAbout = el.getAttribute( ABOUT );
          const updatedAbout = oldAbout.replace( /#.*$/, `#${ newId }` );
          el.setAttribute( ABOUT, updatedAbout );
        }
      } );
    } );
    const updatedView = this.editor.data.processor.toView( parser.body.innerHTML );
    // Replace the _children directly
    fragment._children = updatedView._children;
  }
}
